import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCurrency, formatDate } from '@/lib/utils';
import {
  getAccountOpportunities,
  createAccountOpportunity,
  updateAccountOpportunity,
  deleteAccountOpportunity,
} from '../api';
import type {
  OpportunityDetail,
  PipelineStage,
  OpportunityType,
  CreateOpportunityInput,
} from '../types';
import { PIPELINE_STAGES } from '@/features/crm/constants';
import OpportunityFormModal from './OpportunityFormModal';

interface OpportunitiesTabProps {
  accountId: string;
}

// Build stage options from CRM constants
const STAGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Stages' },
  ...PIPELINE_STAGES.map((stage) => ({
    value: stage.key,
    label: stage.label,
  })),
];

// Map stages to badge variants
const STAGE_VARIANTS: Record<PipelineStage, 'secondary' | 'warning' | 'success' | 'destructive' | 'outline'> = {
  prospect_acquired: 'secondary',
  first_contact: 'secondary',
  discovery_meeting: 'warning',
  proposal_drafting: 'warning',
  proposal_presented: 'outline',
  quote_created: 'success',
  lost: 'destructive',
};

const TYPE_LABELS: Record<OpportunityType, string> = {
  new_business: 'New Business',
  expansion: 'Expansion',
  upgrade: 'Upgrade',
  'cross-sell': 'Cross-sell',
  renewal: 'Renewal',
  other: 'Other',
};

const PRIORITY_STYLES: Record<string, string> = {
  high: 'border-l-4 border-l-red-500',
  medium: 'border-l-4 border-l-orange-400',
  low: 'border-l-4 border-l-blue-400',
};

// Map old status values to new pipeline stages for backwards compatibility
function mapStatusToStage(status: string): PipelineStage {
  const mapping: Record<string, PipelineStage> = {
    identified: 'prospect_acquired',
    in_progress: 'first_contact',
    won: 'quote_created',
    lost: 'lost',
    dismissed: 'lost',
  };
  return mapping[status] ?? 'prospect_acquired';
}

export default function OpportunitiesTab({ accountId }: OpportunitiesTabProps) {
  const queryClient = useQueryClient();
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<OpportunityDetail | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-opportunities', accountId, stageFilter],
    queryFn: () =>
      getAccountOpportunities(accountId, {
        stage: stageFilter === 'all' ? undefined : stageFilter,
      }),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateOpportunityInput) =>
      createAccountOpportunity(accountId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-opportunities', accountId] });
      setIsModalOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateOpportunityInput> & { status?: string } }) =>
      updateAccountOpportunity(accountId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-opportunities', accountId] });
      setIsModalOpen(false);
      setEditingOpportunity(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAccountOpportunity(accountId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-opportunities', accountId] });
    },
  });

  if (isLoading) {
    return <OpportunitiesTabSkeleton />;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading opportunities</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const opportunities = data?.opportunities || [];
  const summary = data?.summary || {
    total: 0,
    auto_detected: 0,
    manual: 0,
    total_estimated_value: 0,
    high_priority: 0,
  };

  const handleOpenAdd = () => {
    setEditingOpportunity(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (opportunity: OpportunityDetail) => {
    setEditingOpportunity(opportunity);
    setIsModalOpen(true);
  };

  const handleSave = async (formData: CreateOpportunityInput) => {
    if (editingOpportunity) {
      await updateMutation.mutateAsync({ id: editingOpportunity.id, data: formData });
    } else {
      await createMutation.mutateAsync(formData);
    }
  };

  const handleStageChange = async (opportunity: OpportunityDetail, newStage: string) => {
    await updateMutation.mutateAsync({
      id: opportunity.id,
      data: { stage: newStage as PipelineStage },
    });
  };

  const handleDelete = async (opportunity: OpportunityDetail) => {
    if (window.confirm(`Are you sure you want to delete "${opportunity.title}"?`)) {
      await deleteMutation.mutateAsync(opportunity.id);
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{summary.total}</div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">
              {formatCurrency(summary.total_estimated_value, 'CAD')}
            </div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Est. Value</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-red-600">{summary.high_priority}</div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">High Priority</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Card with Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Opportunities</CardTitle>
              <CardDescription>
                {summary.total} opportunit{summary.total !== 1 ? 'ies' : 'y'} found
                {summary.auto_detected > 0 && ` (${summary.auto_detected} auto-detected)`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={stageFilter}
                onValueChange={(v) => setStageFilter(v)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Filter stage" />
                </SelectTrigger>
                <SelectContent>
                  {STAGE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={handleOpenAdd}>
                + Add
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {opportunities.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No opportunities detected
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>HubSpot</TableHead>
                  <TableHead>Close Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {opportunities.map((opportunity) => (
                  <OpportunityRow
                    key={opportunity.id}
                    opportunity={opportunity}
                    onStageChange={handleStageChange}
                    onEdit={handleOpenEdit}
                    onDelete={handleDelete}
                    isUpdating={updateMutation.isPending}
                    isDeleting={deleteMutation.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <OpportunityFormModal
        open={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) setEditingOpportunity(null);
        }}
        opportunity={editingOpportunity}
        onSave={handleSave}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}

interface OpportunityRowProps {
  opportunity: OpportunityDetail;
  onStageChange: (opportunity: OpportunityDetail, stage: string) => void;
  onEdit: (opportunity: OpportunityDetail) => void;
  onDelete: (opportunity: OpportunityDetail) => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

function OpportunityRow({
  opportunity,
  onStageChange,
  onEdit,
  onDelete,
  isUpdating,
  isDeleting,
}: OpportunityRowProps) {
  const isAutoDetected = opportunity.auto_detected;
  const priorityStyle = opportunity.priority ? PRIORITY_STYLES[opportunity.priority] : '';
  const hasHubSpot = !!opportunity.hubspot_deal_id;

  // Use stage if available, otherwise fallback to status for backwards compatibility
  const currentStage = opportunity.stage ?? mapStatusToStage(opportunity.status);

  // Use HubSpot amount if available, otherwise estimated_value
  const displayValue = opportunity.hubspot_amount ?? opportunity.estimated_value;

  // Use HubSpot close date if available, otherwise target_date
  const closeDate = opportunity.hubspot_close_date ?? opportunity.target_date;

  // Format HubSpot stage for display (replace underscores, title case)
  const formatStage = (stage: string | undefined) => {
    if (!stage) return null;
    return stage.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <TableRow className={priorityStyle}>
      <TableCell>
        <div className="flex items-center gap-2">
          <span className="font-medium">{opportunity.title}</span>
          {isAutoDetected && (
            <Badge variant="outline" className="text-purple-600 border-purple-300">
              Auto
            </Badge>
          )}
        </div>
        {opportunity.description && (
          <p className="text-sm text-muted-foreground mt-1 truncate max-w-[300px]">
            {opportunity.description}
          </p>
        )}
      </TableCell>
      <TableCell>
        <Badge variant="secondary">{TYPE_LABELS[opportunity.type]}</Badge>
      </TableCell>
      <TableCell>
        {isAutoDetected ? (
          <Badge variant={STAGE_VARIANTS[currentStage] ?? 'secondary'}>
            {PIPELINE_STAGES.find((s) => s.key === currentStage)?.label ?? currentStage}
          </Badge>
        ) : (
          <Select
            value={currentStage}
            onValueChange={(v) => onStageChange(opportunity, v)}
            disabled={isUpdating}
          >
            <SelectTrigger className="w-[160px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PIPELINE_STAGES.filter((s) => !s.isTerminal || s.key === currentStage).map((stage) => (
                <SelectItem key={stage.key} value={stage.key}>
                  {stage.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </TableCell>
      <TableCell className="text-right">
        {displayValue ? formatCurrency(displayValue, 'CAD') : '—'}
      </TableCell>
      <TableCell>
        {hasHubSpot ? (
          <div className="space-y-1">
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              HubSpot
            </Badge>
            {opportunity.hubspot_deal_stage && (
              <p className="text-xs text-muted-foreground">
                {formatStage(opportunity.hubspot_deal_stage)}
              </p>
            )}
            {opportunity.hubspot_is_won && (
              <Badge variant="success" className="text-xs">Won</Badge>
            )}
            {opportunity.hubspot_is_closed && !opportunity.hubspot_is_won && (
              <Badge variant="destructive" className="text-xs">Lost</Badge>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </TableCell>
      <TableCell>
        {closeDate ? formatDate(closeDate) : '—'}
      </TableCell>
      <TableCell>
        {!isAutoDetected && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(opportunity)}
              disabled={isUpdating}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(opportunity)}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

function OpportunitiesTabSkeleton() {
  return (
    <div className="space-y-4">
      {/* Summary Cards Skeleton */}
      <div className="grid gap-4 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-4 w-16 mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Card Skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-24 mt-1" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
